// File: src/pages/OrderList.jsx
import React, { useEffect, useMemo, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Row,
  Col,
  Input,
  Button,
  Table,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from "reactstrap";
import swal from "sweetalert";

import UserContext from "../import/context/Context";
import SuperAdminUI from "../import/SuperAdminUI";
import { CheckPermission } from "../import/CheckPermission";
import { _Get, _Delete } from "../import/ApiEndPoint/ApiCalling";

import { Delete_Sales_order, Delete_Sales } from "../import/ApiEndPoint/Api";

const clearGlobalSalesFastCaches = () => {
  window.__dmsSalesPageSummaryCache?.clear?.();
  window.__dmsSalesPageBootstrapCache?.clear?.();
  window.__dmsSalesPartyLedgerCache?.clear?.();
};

/* =========================
   Helpers
   ========================= */
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
};
const N = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const S = (v) => (v == null ? "" : String(v));
const normalize = (s) => S(s).trim().toLowerCase();

const statusOf = (o) =>
  normalize(o.status ?? o.orderStatus ?? o.state ?? o.currentStatus);
const isPendingOnly = (o) => statusOf(o).includes("pending");

const coerceOrderNo = (o) =>
  o.orderNo ?? o.orderNumber ?? o.order_number ?? o.code ?? o.orderid ?? o.id;

// Derive all money/tax totals quickly without extra calls
function patchOrderTotals(order) {
  const o = JSON.parse(JSON.stringify(order || {}));

  // The optimized list endpoint intentionally omits orderItems. In that case
  // use the totals already persisted on the order instead of recalculating them.
  if (!Array.isArray(o.orderItems) || o.orderItems.length === 0) {
    o.amount = N(o.amount ?? o.basicPrice);
    o.cgstTotal = N(o.cgstTotal);
    o.sgstTotal = N(o.sgstTotal);
    o.igstTotal = N(o.igstTotal);
    o.gstTotal = +(o.cgstTotal + o.sgstTotal + o.igstTotal).toFixed(2);
    o.grandTotal = N(o.grandTotal);
    return o;
  }

  let basic = 0,
    cgst = 0,
    sgst = 0,
    igst = 0;

  (o.orderItems || []).forEach((it) => {
    const qty = N(it.qty);
    const price = N(
      it.BasicPrice ??
        it.price ??
        it?.productId?.SalesRate ??
        it?.productId?.Product_MRP,
    );
    const lineBasic = +(price * qty).toFixed(2);

    const gst = N(it.gstPercentage ?? it.gst ?? it?.productId?.GSTRate);
    if (o.igstTaxType === 1) {
      it.igstTotal = +((lineBasic * gst) / 100).toFixed(2);
      it.cgstTotal = 0;
      it.sgstTotal = 0;
    } else {
      it.cgstTotal = +((lineBasic * (gst / 2)) / 100).toFixed(2);
      it.sgstTotal = +((lineBasic * (gst / 2)) / 100).toFixed(2);
      it.igstTotal = 0;
    }
    it.BasicPrice = +price.toFixed(2);
    it.BasicTotal = lineBasic;
    it.grandTotal = +(
      lineBasic +
      it.cgstTotal +
      it.sgstTotal +
      it.igstTotal
    ).toFixed(2);

    it.primaryUnit =
      it.primaryUnit ||
      it?.productId?.PrimaryUnit ||
      it?.productId?.primaryUnit ||
      "";
    basic += lineBasic;
    cgst += it.cgstTotal;
    sgst += it.sgstTotal;
    igst += it.igstTotal;
  });

  o.amount = +basic.toFixed(2);
  o.cgstTotal = +cgst.toFixed(2);
  o.sgstTotal = +sgst.toFixed(2);
  o.igstTotal = +igst.toFixed(2);
  o.gstTotal = +(cgst + sgst + igst).toFixed(2);
  o.grandTotal = +(basic + o.gstTotal).toFixed(2);
  return o;
}

// Robust, zero-extra-call way to pick sales mobile
const getSalesMobile = (r) => {
  const cand = [
    r?.salesPersonMobile,
    r?.created_by?.mobile,
    r?.created_by?.phone,
    r?.created_by?.contactNumber,
    r?.userId?.mobile,
    r?.userId?.phone,
    r?.userId?.contactNumber,
  ];
  const m = cand.find((v) => S(v).trim().length > 0);
  return m ? String(m) : "—";
};

/* =========================
   Component
   ========================= */
export default function OrderList() {
  const navigate = useNavigate();
  const ctx = useContext(UserContext);
  const perms = CheckPermission("Sales Order");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const [masterShow, setMasterShow] = useState(false);

  // ⚡ Removed “Available” forever for speed and clarity
  const defaultColumns = [
    "Actions",
    "Status",
    "Order No",
    "Order Date/Time",
    "Party Name",
    "Sales Person Mobile",
    "Taxable",
    "GST Total",
    "Grand Total",
  ];

  // Clean any legacy “Available” from saved prefs
  const readVisible = () => {
    const saved = JSON.parse(localStorage.getItem("OrderListshow"));
    if (!Array.isArray(saved) || saved.length === 0) return defaultColumns;
    const cleaned = saved.filter((c) => c !== "Available");
    // Ensure only known columns and preserve order based on defaultColumns
    const inSet = new Set(cleaned);
    const merged = defaultColumns.filter((c) => inSet.has(c));
    // Add any saved unknowns at the end (unlikely)
    cleaned.forEach((c) => {
      if (!merged.includes(c) && defaultColumns.includes(c)) merged.push(c);
    });
    return merged.length ? merged : defaultColumns;
  };

  const [colModalOpen, setColModalOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(readVisible);

  // NEW: Remark modal state
  const [remarkOpen, setRemarkOpen] = useState(false);
  const [remarkText, setRemarkText] = useState("");
  const [remarkOrderNo, setRemarkOrderNo] = useState("");

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData")) || {};
    if (user?.rolename?.roleName === "MASTER") setMasterShow(true);
    loadData(user._id, user.database);
    // eslint-disable-next-line
  }, []);

  // ⚡ Fast load: one API call, light processing, no per-party calls
  async function loadData(userId, db) {
    setLoading(true);
    try {
      const res = await _Get(
        `/order/sales-order-list-lite/${encodeURIComponent(userId)}/`,
        encodeURIComponent(db),
      );

      let list = (res?.orderHistory || []).map((raw) => {
        const orderNo = coerceOrderNo(raw);
        return patchOrderTotals({ ...raw, orderNo });
      });

      // Only "pending"
      list = list.filter(isPendingOnly);

      // Sort newest first
      list.sort(
        (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
      );

      setRows(list);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    if (!query) return rows;
    const q = query.toLowerCase();
    return rows.filter((r) => {
      const party = S(r?.partyId?.CompanyName).toLowerCase();
      const ono = S(r?.orderNo).toLowerCase();
      const spm = getSalesMobile(r).toLowerCase();
      return party.includes(q) || ono.includes(q) || spm.includes(q);
    });
  }, [rows, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const current = filtered.slice((page - 1) * pageSize, page * pageSize);

  function openRemarkModal(order) {
    setRemarkText(S(order?.Remark));
    setRemarkOrderNo(S(order?.orderNo) || "—");
    setRemarkOpen(true);
  }

  async function handleDelete(row) {
    const isPending = isPendingOnly(row);
    const api = isPending ? Delete_Sales_order : Delete_Sales;

    const ok = await swal({
      title: "Warning",
      text: "Sure you want to delete this order?",
      buttons: { cancel: "Cancel", confirm: "Delete" },
      icon: "warning",
    });
    if (ok !== true) return;

    try {
      await _Delete(api, row?._id);
      setRows((previous) => previous.filter((item) => item?._id !== row?._id));
      clearGlobalSalesFastCaches();
      swal("Deleted", "Order removed.", "success");
    } catch (err) {
      swal("Error", err?.response?.data?.message || "Delete failed", "error");
    }
  }

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary" />
      </div>
    );
  }

  return (
    <>
      <div className="p-3" style={{ fontSize: 12 }}>
        <Card>
          <CardBody>
            {/* Toolbar */}
            <Row className="align-items-center" style={{ marginBottom: 8 }}>
              <Col>
                <h3
                  style={{
                    fontWeight: 600,
                    textTransform: "uppercase",
                    fontSize: 14,
                    margin: 0,
                  }}
                >
                  Sales Order — Pending Only ({rows.length})
                </h3>
              </Col>

              {masterShow ? (
                <Col lg="2" md="4" sm="12">
                  <SuperAdminUI
                    onDropdownChange={() => {}}
                    onSubmit={() => {
                      const u = JSON.parse(localStorage.getItem("userData"));
                      if (u) loadData(u._id, u.database);
                    }}
                  />
                </Col>
              ) : (
                <Col />
              )}

              <Col lg="3" md="6" sm="12">
                <Input
                  bsSize="sm"
                  placeholder="Search order no / party / sales mobile..."
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setPage(1);
                  }}
                  value={query}
                />
              </Col>

              <Col lg="3" className="d-flex justify-content-end">
                <Button
                  color="link"
                  size="sm"
                  onClick={() => setColModalOpen(true)}
                  style={{ textDecoration: "none", padding: 0 }}
                >
                  Manage Columns
                </Button>
              </Col>
            </Row>

            {/* Table */}
            <Table bordered responsive size="sm" style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th>#</th>
                  {visibleColumns.includes("Actions") && <th>Actions</th>}
                  {visibleColumns.includes("Status") && <th>Status</th>}
                  {visibleColumns.includes("Order No") && <th>Order No</th>}
                  {visibleColumns.includes("Order Date/Time") && (
                    <th>Order Date/Time</th>
                  )}
                  {visibleColumns.includes("Party Name") && <th>Party Name</th>}
                  {visibleColumns.includes("Sales Person Mobile") && (
                    <th>Sales Person Mobile</th>
                  )}
                  {visibleColumns.includes("Taxable") && <th>Taxable</th>}
                  {visibleColumns.includes("GST Total") && <th>GST Total</th>}
                  {visibleColumns.includes("Grand Total") && (
                    <th>Grand Total</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {current.map((r, idx) => {
                  const salesMobile = getSalesMobile(r);
                  const hasRemark = S(r?.Remark).trim().length > 0;

                  return (
                    <tr key={r._id}>
                      <td>{(page - 1) * pageSize + idx + 1}</td>

                      {visibleColumns.includes("Actions") && (
                        <td style={{ whiteSpace: "nowrap" }}>
                          {perms?.Edit && (
                            <Button
                              size="sm"
                              color="primary"
                              style={{ padding: "2px 8px" }}
                              onClick={() =>
                                navigate(`/app/sales-orders/edit/${r._id}`, {
                                  state: { fromOrder: true, order: r },
                                })
                              }
                            >
                              Edit
                            </Button>
                          )}{" "}
                          {perms?.Delete && (
                            <Button
                              size="sm"
                              color="danger"
                              style={{ padding: "2px 8px" }}
                              onClick={() => handleDelete(r)}
                            >
                              Delete
                            </Button>
                          )}{" "}
                          {hasRemark && (
                            <Button
                              size="sm"
                              color="warning"
                              style={{ padding: "2px 8px" }}
                              onClick={() => openRemarkModal(r)}
                            >
                              Remark
                            </Button>
                          )}
                        </td>
                      )}

                      {visibleColumns.includes("Status") && (
                        <td>
                          <Badge color="secondary">{r.status}</Badge>
                        </td>
                      )}

                      {visibleColumns.includes("Order No") && (
                        <td>{S(r.orderNo) || "—"}</td>
                      )}
                      {visibleColumns.includes("Order Date/Time") && (
                        <td>{fmtDateTime(r.createdAt)}</td>
                      )}
                      {visibleColumns.includes("Party Name") && (
                        <td>{r?.partyId?.CompanyName || "—"}</td>
                      )}
                      {visibleColumns.includes("Sales Person Mobile") && (
                        <td>{salesMobile}</td>
                      )}
                      {visibleColumns.includes("Taxable") && (
                        <td>{N(r.amount).toFixed(2)}</td>
                      )}
                      {visibleColumns.includes("GST Total") && (
                        <td>
                          {N(r.cgstTotal + r.sgstTotal + r.igstTotal).toFixed(
                            2,
                          )}
                        </td>
                      )}
                      {visibleColumns.includes("Grand Total") && (
                        <td>{N(r.grandTotal).toFixed(2)}</td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            {/* Pagination */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 12,
                marginTop: 6,
              }}
            >
              <div>
                Page {page} / {totalPages}
              </div>
              <div>
                <Button
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Prev
                </Button>{" "}
                <Button
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Manage columns modal */}
      <Modal isOpen={colModalOpen} toggle={() => setColModalOpen(false)}>
        <ModalHeader toggle={() => setColModalOpen(false)}>
          Change Fields
        </ModalHeader>
        <ModalBody>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {defaultColumns.map((col) => (
              <label key={col} style={{ fontSize: 12 }}>
                <input
                  type="checkbox"
                  checked={visibleColumns.includes(col)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setVisibleColumns((v) =>
                        Array.from(new Set([...v, col])),
                      );
                    } else {
                      setVisibleColumns((v) => v.filter((x) => x !== col));
                    }
                  }}
                />{" "}
                {col}
              </label>
            ))}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() => {
              // never persist legacy "Available"
              const clean = visibleColumns.filter((c) => c !== "Available");
              localStorage.setItem("OrderListshow", JSON.stringify(clean));
              setColModalOpen(false);
            }}
          >
            Submit
          </Button>
          <Button color="secondary" onClick={() => setColModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Remark viewer modal */}
      <Modal isOpen={remarkOpen} toggle={() => setRemarkOpen(false)}>
        <ModalHeader toggle={() => setRemarkOpen(false)}>
          Remark {remarkOrderNo ? `— ${remarkOrderNo}` : ""}
        </ModalHeader>
        <ModalBody>
          <div
            style={{
              whiteSpace: "pre-wrap",
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            {remarkText || "No remark available."}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setRemarkOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
